import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '@/components/common/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import {
  GET_TICKET,
  AGREGAR_COMENTARIO,
  ACTUALIZAR_TICKET,
  CERRAR_TICKET,
  EVALUAR_SLA,
} from './gql.js';

const ESTADOS = [
  { value: 'NUEVO', label: 'Nuevo' },
  { value: 'EN_PROGRESO', label: 'En progreso' },
  { value: 'PENDIENTE_CLIENTE', label: 'Pendiente cliente' },
  { value: 'RESUELTO', label: 'Resuelto' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'ANULADO', label: 'Anulado' },
];

export default function TicketDetail({ ticketId, onBack }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [comentario, setComentario] = useState('');
  const [estadoObjetivo, setEstadoObjetivo] = useState('');
  const [{ data, fetching, error }] = useQuery({
    query: GET_TICKET,
    variables: { id: ticketId },
    pause: !ticketId,
    requestPolicy: 'cache-and-network',
  });
  const [, agregarComentario] = useMutation(AGREGAR_COMENTARIO);
  const [, actualizarTicket] = useMutation(ACTUALIZAR_TICKET);
  const [, cerrarTicket] = useMutation(CERRAR_TICKET);
  const [evaluarResult, reexecuteEvaluar] = useQuery({
    query: EVALUAR_SLA,
    variables: { ticketId },
    pause: !ticketId,
  });

  const ticket = data?.getTicket;
  const slaEvaluado = evaluarResult.data?.evaluarSLA?.slaAplicada ?? ticket?.sla;

  const timeline = useMemo(() => {
    if (!ticket) return [];
    const items = [
      ...(ticket.historial ?? []).map(item => ({
        id: `historial-${item.id}`,
        tipo: 'historial',
        fecha: item.creadoEn,
        label: `${item.campo} → ${item.valorNuevo ?? ''}`,
        detalle: item.valorAnterior ? `Antes: ${item.valorAnterior}` : null,
      })),
      ...(ticket.comentarios ?? []).map(item => ({
        id: `comentario-${item.id}`,
        tipo: 'comentario',
        fecha: item.creadoEn,
        label: item.autorTipo === 'CLIENTE' ? 'Comentario cliente' : item.autorTipo === 'AGENTE' ? 'Respuesta agente' : 'Sistema',
        detalle: item.mensaje,
        privado: item.privado,
      })),
    ];
    return items.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  }, [ticket]);

  const handleAgregarComentario = async (event) => {
    event.preventDefault();
    if (!comentario.trim()) return;
    const result = await agregarComentario({
      input: {
        ticketId,
        mensaje: comentario,
        privado: false,
        autorTipo: 'AGENTE',
      },
    });
    if (result.error) {
      alert(result.error.message);
      return;
    }
    setComentario('');
    setRefreshKey(prev => prev + 1);
  };

  const handleActualizarEstado = async () => {
    if (!estadoObjetivo) return;
    const result = await actualizarTicket({ id: ticketId, input: { estado: estadoObjetivo } });
    if (result.error) {
      alert(result.error.message);
      return;
    }
    setEstadoObjetivo('');
    setRefreshKey(prev => prev + 1);
  };

  const handleCerrarTicket = async () => {
    const resolucion = prompt('Detalle de resolución');
    if (!resolucion) return;
    const result = await cerrarTicket({ input: { id: ticketId, resolucion } });
    if (result.error) {
      alert(result.error.message);
      return;
    }
    setRefreshKey(prev => prev + 1);
  };

  const handleEvaluarSla = () => {
    reexecuteEvaluar({ requestPolicy: 'network-only' });
  };

  return (
    <div className="w-full" key={refreshKey}>
      <PageHeader
        title={ticket ? `Ticket #${ticket.id}` : 'Ticket'}
        subtitle={ticket?.asunto ?? 'Detalle de ticket'}
        actions={(
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition hover:bg-[rgba(var(--subpanel),0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Volver
          </button>
        )}
      />

      <section className="mx-auto mt-6 grid w-full max-w-[1400px] grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-4">
          <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Descripción</h2>
            <p className="mt-2 text-sm leading-relaxed text-[rgba(var(--muted),0.95)]">
              {ticket?.descripcion || 'Sin descripción proporcionada.'}
            </p>
            <dl className="mt-6 grid grid-cols-2 gap-3 text-sm text-[rgba(var(--muted),0.95)]">
              <div>
                <dt className="font-semibold text-[rgb(var(--fg))]">Estado</dt>
                <dd>{ticket?.estado}</dd>
              </div>
              <div>
                <dt className="font-semibold text-[rgb(var(--fg))]">Prioridad</dt>
                <dd>{ticket?.prioridad}</dd>
              </div>
              <div>
                <dt className="font-semibold text-[rgb(var(--fg))]">Cliente</dt>
                <dd>#{ticket?.clienteId}</dd>
              </div>
              <div>
                <dt className="font-semibold text-[rgb(var(--fg))]">Creado</dt>
                <dd>{ticket ? new Date(ticket.creadoEn).toLocaleString() : '-'}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Timeline</h2>
                <p className="text-sm text-[rgba(var(--muted),0.9)]">Seguimiento de actividades y comentarios</p>
              </div>
            </header>
            <div className="mt-4 flex flex-col gap-4">
              {timeline.length === 0 ? (
                <EmptyState title="Sin actividad" description="Todavía no hay comentarios ni cambios registrados." />
              ) : (
                timeline.map(item => (
                  <article
                    key={item.id}
                    className="rounded-xl border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.65)] p-4 text-sm text-[rgb(var(--fg))] shadow-inner"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{item.label}</span>
                      <time className="text-xs text-[rgba(var(--muted),0.9)]">{new Date(item.fecha).toLocaleString()}</time>
                    </div>
                    {item.detalle ? (
                      <p className="mt-2 whitespace-pre-line text-sm text-[rgba(var(--muted),0.95)]">{item.detalle}</p>
                    ) : null}
                    {item.tipo === 'comentario' ? (
                      <p className="mt-2 whitespace-pre-line text-sm text-[rgba(var(--muted),0.95)]">{item.detalle}</p>
                    ) : null}
                    {item.privado ? (
                      <span className="mt-2 inline-flex rounded-full bg-rose-500/20 px-2 py-0.5 text-xs font-semibold text-rose-500">
                        Privado
                      </span>
                    ) : null}
                  </article>
                ))
              )}
            </div>
          </div>

          <form
            onSubmit={handleAgregarComentario}
            className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl"
          >
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Agregar comentario</h2>
            <Field htmlFor="comentario" hint="Los comentarios son visibles para el cliente salvo que se marquen como privados">
              <textarea
                id="comentario"
                value={comentario}
                onChange={event => setComentario(event.target.value)}
                rows={4}
                className="mt-3 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                Enviar comentario
              </button>
            </div>
          </form>
        </div>

        <aside className="col-span-12 space-y-4 lg:col-span-4">
          <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">SLA</h2>
            {slaEvaluado ? (
              <dl className="mt-4 space-y-2 text-sm text-[rgba(var(--muted),0.95)]">
                <div>
                  <dt className="font-semibold text-[rgb(var(--fg))]">Nombre</dt>
                  <dd>{slaEvaluado.slaNombre ?? '—'}</dd>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <dt className="font-semibold text-[rgb(var(--fg))]">Promesa (h)</dt>
                    <dd>{slaEvaluado.promesaHoras ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-[rgb(var(--fg))]">1ª respuesta (min)</dt>
                    <dd>{slaEvaluado.primeraRespuestaMin ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-[rgb(var(--fg))]">MTTA</dt>
                    <dd>{slaEvaluado.mttaMin != null ? `${slaEvaluado.mttaMin} min` : '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold text-[rgb(var(--fg))]">MTTR</dt>
                    <dd>{slaEvaluado.mttrMin != null ? `${slaEvaluado.mttrMin} min` : '—'}</dd>
                  </div>
                </div>
                <div>
                  <dt className="font-semibold text-[rgb(var(--fg))]">Cumplido</dt>
                  <dd>
                    {slaEvaluado.cumplido == null ? 'Pendiente' : slaEvaluado.cumplido ? 'Sí' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-[rgb(var(--fg))]">Última evaluación</dt>
                  <dd>{slaEvaluado.evaluadoEn ? new Date(slaEvaluado.evaluadoEn).toLocaleString() : '—'}</dd>
                </div>
              </dl>
            ) : (
              <EmptyState title="Sin SLA" description="Asigná un SLA para comenzar a medir cumplimiento." />
            )}
            <button
              type="button"
              onClick={handleEvaluarSla}
              className="mt-4 w-full rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Recalcular métricas
            </button>
          </div>

          <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Gestión rápida</h2>
            <div className="mt-4 space-y-3">
              <div>
                <label htmlFor="estadoObjetivo" className="text-sm font-semibold text-[rgb(var(--fg))]">
                  Cambiar estado
                </label>
                <select
                  id="estadoObjetivo"
                  value={estadoObjetivo}
                  onChange={event => setEstadoObjetivo(event.target.value)}
                  className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  <option value="">Seleccionar estado</option>
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleActualizarEstado}
                  className="mt-3 w-full rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Actualizar estado
                </button>
              </div>
              <button
                type="button"
                onClick={handleCerrarTicket}
                className="w-full rounded-xl border border-rose-400 px-4 py-2 text-sm font-semibold text-rose-500 transition hover:bg-rose-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                Cerrar ticket
              </button>
            </div>
          </div>
        </aside>
      </section>

      <StickyActions>
        <div className="flex w-full items-center justify-between gap-4 text-sm text-[rgba(var(--muted),0.9)]">
          <div>
            Estado actual: <span className="font-semibold text-[rgb(var(--fg))]">{ticket?.estado ?? '—'}</span>
          </div>
          <button
            type="button"
            onClick={() => setRefreshKey(prev => prev + 1)}
            className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Refrescar datos
          </button>
        </div>
      </StickyActions>
    </div>
  );
}
